import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import './Taxonomy.scss';
import { PresentationTreeNetwork, TaxonomyChild } from 'app/api/tag-queries/types';
import { TagText } from './TaxonomyItemText';
import { TaxonomyItem } from './TaxonomyItem';
import { getConceptTypeContent, getTagLabel, formatDefinition } from 'app/utils/tag-utils';
import { Typography } from 'app/components/ui';
import { TagContext } from 'app/state/contexts/TagContext';
import { useProgressiveRender } from './useProgressiveRender';

const Arrow = ({ isOpen }: { isOpen: boolean }) => {
  return (
    <div className={`taxonomy__icon ${isOpen ? '' : 'icon-closed'}`}>
      <i className="fa-solid fa-caret-down"></i>
    </div>
  );
};

interface TaxonomyWrapperProps extends PresentationTreeNetwork {
  selectedTagId: string | null;
  selectedNodePath: string | null;
  expandToIds: Set<string> | null;
  searchExpandIds?: Set<string> | null;
  searchVisibleIds?: Set<string> | null;
  isSearching?: boolean;
  onSelectTag: (tag: TaxonomyChild, nodePath?: string) => void;
  onDetailsClick?: (tag: TaxonomyChild) => void;
  onExtensionClick?: (tagId: string) => void;
  usedTags?: string[];
  extensionTags?: string[];
  aliasOverride?: string;
}

export const TaxonomyWrapper = React.memo(
  ({
    linkrole,
    roots,
    onSelectTag,
    selectedTagId,
    selectedNodePath,
    expandToIds,
    searchExpandIds,
    searchVisibleIds,
    isSearching,
    onDetailsClick,
    onExtensionClick,
    usedTags = [],
    extensionTags: extensionTagsProp,
    aliasOverride,
  }: TaxonomyWrapperProps) => {
    const [isOpen, setIsOpen] = useState(expandToIds !== null);

    const prevIsSearching = useRef(isSearching);
    useEffect(() => {
      if (isSearching && !prevIsSearching.current) setIsOpen(true);
      prevIsSearching.current = !!isSearching;
    }, [isSearching]);

    const effectiveIsOpen = isOpen;

    const { tagLanguage } = useContext(TagContext);
    const linkroleLabel = useMemo(() => linkrole.labels?.[tagLanguage] ?? linkrole.labels?.en ?? undefined, [linkrole.labels, tagLanguage]);
    const { category, label } = useMemo(() => {
      if (aliasOverride && aliasOverride.length > 0) return formatDefinition(aliasOverride);
      return formatDefinition(linkrole.definition, linkroleLabel);
    }, [aliasOverride, linkrole.definition, linkroleLabel]);
    const descriptiveLabel = useMemo(() => getTagLabel({ languages: roots[0].labels, currentLang: tagLanguage }), [roots, tagLanguage]);
    const { conceptLabel, icon } = useMemo(() => getConceptTypeContent(roots[0].conceptType), [roots]);

    const extensionTags = useMemo(() => extensionTagsProp ?? [], [extensionTagsProp]);

    const rootTaxonomyItem = useMemo<TaxonomyChild>(() => ({ ...roots[0], order: 0 }), [roots]);
    const isRootAbstract = roots[0].isAbstract;
    const isRootUsed = useMemo(() => usedTags.includes(roots[0].id), [usedTags, roots]);
    const isRootExtension = useMemo(() => extensionTags.includes(roots[0].id), [extensionTags, roots]);
    const rootNodePath = `${linkrole.roleURI}>${roots[0].id}`;
    const isRootSelected = selectedTagId === roots[0].id && (selectedNodePath === null || selectedNodePath === rootNodePath);

    const handleToggle = useCallback(() => setIsOpen((prev) => !prev), []);
    const handleRootDetails = useCallback(() => onDetailsClick?.(rootTaxonomyItem), [onDetailsClick, rootTaxonomyItem]);
    const handleRootSelect = useCallback(() => {
      if (!isRootAbstract) onSelectTag?.(rootTaxonomyItem, rootNodePath);
    }, [isRootAbstract, onSelectTag, rootTaxonomyItem, rootNodePath]);
    const handleRootExtension = useCallback(() => onExtensionClick?.(roots[0].id), [onExtensionClick, roots]);

    const filteredRootChildren = useMemo(
      () => (isSearching && searchVisibleIds ? roots[0].children.filter((c) => searchVisibleIds.has(c.id)) : roots[0].children),
      [roots, isSearching, searchVisibleIds]
    );
    const visibleChildren = useProgressiveRender(filteredRootChildren, effectiveIsOpen);

    return (
      <ul className={`taxonomy ${effectiveIsOpen ? 'border-gray-300' : ''}`}>
        <li className={`taxonomy__list-item ${effectiveIsOpen ? 'bg-custom-dark border-bottom' : 'bg-white'}`} onClick={handleToggle}>
          <Arrow isOpen={effectiveIsOpen} />

          <div>
            <Typography variant="body-3">{category}</Typography>
            <Typography variant="body-7">{label}</Typography>
          </div>
        </li>

        {effectiveIsOpen && (
          <ul className="taxonomy__child-wrapper pr-8">
            <li className="taxonomy__list-wrapper pos-rel">
              <span className="taxonomy__prefix-line" />

              <div className="flex-row" onClick={handleRootSelect}>
                <TagText
                  label={descriptiveLabel || ''}
                  conceptLabel={conceptLabel}
                  icon={icon}
                  isUsed={isRootUsed}
                  isExtension={isRootExtension}
                  isSelected={isRootSelected}
                  onDetailsClick={onDetailsClick && handleRootDetails}
                  onExtensionClick={onExtensionClick && handleRootExtension}
                />
              </div>
            </li>

            <ul className="taxonomy__child-wrapper">
              {visibleChildren.map((child, idx) => (
                <TaxonomyItem
                  key={`${child.id}|${idx}`}
                  {...child}
                  level={1}
                  usedTags={usedTags}
                  extensionTags={extensionTags}
                  onSelectTag={onSelectTag}
                  selectedTagId={selectedTagId}
                  nodePath={`${rootNodePath}>${child.id}`}
                  selectedNodePath={selectedNodePath}
                  expandToIds={expandToIds}
                  searchExpandIds={searchExpandIds}
                  searchVisibleIds={searchVisibleIds}
                  isSearching={isSearching}
                  onDetailsClick={onDetailsClick}
                  onExtensionClick={onExtensionClick}
                />
              ))}
            </ul>
          </ul>
        )}
      </ul>
    );
  }
);
